import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router';
import axios from 'axios';
import { Apis } from '../util/Apis';
import ContainerBackgroundWrapper from '../components/Backgrounds';
import tiempoAnimacion from '../assets/dataSala/tiempoAnimacion.json';
import { SiteContext } from '../context/SiteContext';
import Button from '../components/atoms/Buttons';
import beepSound from '../assets/sounds/beep.wav';
import VirtualRaffleScene from '../components/virtualRaffle/VirtualRaffleScene';
import RaffleLoading from '../components/virtualRaffle/RaffleLoading';
import RaffleWinnerOverlay from '../components/virtualRaffle/RaffleWinnerOverlay';
import RaffleWinnerFallback from '../components/virtualRaffle/RaffleWinnerFallback';
import RaffleEmptyContent from '../components/virtualRaffle/RaffleEmptyContent';
import { hasRaffleWinner } from '../components/virtualRaffle/raffleResult';
import stageImage from '../assets/virtual-raffle/loading-stage.webp';

export const RaffleStage = styled.div`
  position: relative; width: 100%; height: 100%; overflow: hidden;
  color: #fff0d7;
`;
const Message = styled.div`
  position: absolute; inset: 0; z-index: 3; display: flex;
  flex-direction: column; justify-content: center; align-items: center;
  gap: 20px; padding: 24px; text-align: center;
  background: linear-gradient(#100e15c9, #100e15ed), url(${stageImage}) center / cover;
  p { max-width: 620px; line-height: 1.6; }
`;
const Count = styled(Message)`
  background: #100e157a;
  strong { font-size: clamp(70px, 15vw, 180px); color: #ffdf9b; }
`;

// One mounted draw owns one POST, including StrictMode re-subscriptions.
function Draw({ id, salaId, salaName, onRestart }) {
  const navigate = useNavigate();
  const config = tiempoAnimacion.find(item => item.salaId === Number(salaId));
  const durationMs = config?.winnerAnimationTime ?? 14000;
  const countdownSeconds = config?.hasCountdown ? Math.ceil((config.countdownTime || 0) / 1000) : 0;
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [sceneReady, setSceneReady] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(null);
  const [finished, setFinished] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const confirmationPending = useRef(false);
  const mounted = useRef(false);
  const fallbackElapsed = useRef(false);
  const [fallbackDone, setFallbackDone] = useState(false);
  const url = `${Apis.SORTEOS}/sorteos/${id}`;
  const running = remaining === 0;
  const done = Boolean(outcome) && !error && (finished || (fallback && fallbackDone));
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if ((!sceneReady && !fallback) || remaining <= 0) return;
    const audio = new Audio(beepSound);audio.volume = 0.4;
    audio.play()?.catch(() => {});
    const timer = setTimeout(() => setRemaining(value => value - 1), 1000);
    return () => { clearTimeout(timer);audio.pause(); };
  }, [sceneReady, fallback, remaining]);

  useEffect(() => {
    if (!running) return;
    let active = true;
    if (!generation.current) {
      const job = { confirmed: false, promise: null };
      generation.current = job;
      job.promise = axios.post(`${url}/generate-winner`, null, { timeout: 60000 }).then(() => {
        job.confirmed = true;
        return axios.get(`${url}/winner`, { timeout: 20000 });
      });
    }
    generation.current.promise.then(({ data }) => {
      if (active) { setError(null);setOutcome({ winner: hasRaffleWinner(data) ? data : null }); }
    }).catch(() => { if (active) setError(generation.current.confirmed ? 'result' : 'generation'); });
    return () => { active = false; };
  }, [running, url, attempt]);

  const complete = () => {
    if (outcome && !error) setFinished(true);
  };

  useEffect(() => {
    if (!fallback || !running || fallbackElapsed.current) return;
    const timer = setTimeout(() => { fallbackElapsed.current = true;setFallbackDone(true); }, durationMs);
    return () => clearTimeout(timer);
  }, [fallback, running, durationMs]);

  const confirmWinner = async () => {
    if (!done || !outcome.winner || confirmationPending.current) return;
    if (!outcome.winner.sorteoWinnerId) { navigate('/sorteo-list');return; }
    confirmationPending.current = true;setConfirming(true);setConfirmError(null);
    try {
      await axios.post(`${Apis.SORTEOS}/sorteo-winner/${outcome.winner.sorteoWinnerId}/confirm`);
      if (mounted.current) navigate('/sorteo-list');
    } catch {
      if (mounted.current) setConfirmError('No se pudo confirmar al ganador. Intenta confirmar nuevamente.');
    } finally {
      confirmationPending.current = false;
      if (mounted.current) setConfirming(false);
    }
  };

  const readResult = () => {
    setError(null);setSceneReady(false);
    generation.current.promise = axios.get(`${url}/winner`, { timeout: 20000 });
    setAttempt(value => value + 1);
  };
  return <ContainerBackgroundWrapper showBackgroundCard={false} revealBackground={(revealing || done) && Boolean(outcome?.winner)}><RaffleStage>
    {!error && !fallback && <VirtualRaffleScene outcome={outcome} salaName={salaName} durationMs={durationMs} running={running}
      onReady={() => setSceneReady(true)} onRevealStart={() => setRevealing(true)} onComplete={complete} onFallback={() => setFallback(true)} />}
    {!error && !fallback && <RaffleLoading ready={sceneReady} />}
    {!error && fallback && !done && <Message role="status"><h1>SORTEO VIRTUAL</h1><p>Esperando el resultado del sorteo…</p></Message>}
    {!error && outcome?.winner && (done || (revealing && !fallback)) && <>
      {fallback && done && <RaffleWinnerFallback winner={outcome.winner} />}
      <RaffleWinnerOverlay winner={outcome.winner} salaId={salaId} onRestart={onRestart} showActions={done}
        onConfirm={confirmWinner} confirming={confirming} error={confirmError} />
    </>}
    {done && !outcome.winner && <RaffleEmptyContent onRestart={onRestart} onBack={() => navigate('/sorteo-list')} />}
    {!error && (sceneReady || fallback) && !running && <Count role="status" aria-live="polite"><p>El sorteo comienza en</p><strong>{remaining}</strong></Count>}
    {error && <Message role="alert">
      <h1>No se pudo consultar el resultado</h1>
      <p>{error === 'result' ? 'El sorteo se realizó, pero no pudimos leer el resultado. Puedes consultarlo nuevamente.' : 'No pudimos confirmar la respuesta del sorteo. Revisa su estado antes de iniciar otro.'}</p>
      {error === 'result' && <Button onClick={readResult}>Consultar resultado</Button>}
      <Button variant="outline" onClick={() => navigate('/sorteo-list')}>Volver a sorteos</Button>
    </Message>}
  </RaffleStage></ContainerBackgroundWrapper>;
}

export default function PlaySorteo() {
  const { id } = useParams();
  const { salaId, info } = useContext(SiteContext);
  const [round, setRound] = useState(0);
  return salaId == null
    ? <ContainerBackgroundWrapper showBackgroundCard={false}><RaffleStage><RaffleLoading /></RaffleStage></ContainerBackgroundWrapper>
    : <Draw key={`${salaId}:${id}:${round}`} id={id} salaId={salaId} salaName={info?.sala?.name}
      onRestart={() => setRound(value => value + 1)} />;
}
